/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getExpensesPendingId } from '@/services/Expenses/request';
import { getPopups } from '@/services/Popups/request';

import { PopupsProps } from '@/context/CustomPopupsContext/types';
import { useUser } from '@/context/UserContext';

import { useCustomPopups } from '@/hooks/useCustomPopups/useCustomPopups';

import PopupTemplate1 from '@/components/CustomPopups/PopupTemplate1';
import PopupTemplate2 from '@/components/CustomPopups/PopupTemplate2';
import PopupTemplate3 from '@/components/CustomPopups/PopupTemplate3';

import { Backdrop, Container, Content } from './styles';

const INTERVAL_TIME_IN_MILLISECONDS = 10 * 60 * 1000;
const TIMEOUT_TIME_IN_MILLISECONDS = 2000;

const CustomPopupsLayout = () => {
  const navigate = useNavigate();
  const { isLoggedIn, userInformation } = useUser();
  const roles = userInformation?.roles || [];
  const { pathname } = useLocation();

  const { popupsList, setPopupsList } = useCustomPopups();
  const [isLogged, setIsLogged] = useState(false);

  const isPartnerRoute = pathname.startsWith('/parceiros');

  const requiresPartnerRegistration = useMemo(() => {
    if (!isPartnerRoute || !userInformation) return false;

    const { cpf, cnpj, partner } = userInformation;

    const missingDocument = !cpf && !cnpj;
    const noProfile = !partner?.profile;
    const noCity = !partner?.city;
    const noProductInterest =
      !partner?.interested_in_products || partner.interested_in_products.length === 0;
    return missingDocument || noProfile || noCity || noProductInterest;
  }, [isPartnerRoute, userInformation]);

  const popupsToDisplay = popupsList.filter((popup) => {
    const isPartnerPopup = popup.target === 'AllPartners';

    if (isPartnerRoute) {
      if (requiresPartnerRegistration) {
        return isPartnerPopup && popup.popup_is_open;
      }
      return isPartnerPopup && popup.popup_is_open;
    }

    return !isPartnerPopup && popup.popup_is_open;
  });

  const loadAllPopups = async () => {
    try {
      const expensesPromise =
        userInformation?.user_id && roles.includes('Host')
          ? getExpensesPendingId(Number(userInformation.user_id))
          : Promise.resolve([]);

      // Each source fails on its own: an error on the pending expenses only
      // hides the pending-expenses popup, and vice versa.
      const [popupsResult, pendingResult] = await Promise.allSettled([
        getPopups(),
        expensesPromise,
      ]);

      const resolvedPopups = popupsResult.status === 'fulfilled' ? popupsResult.value : undefined;
      const pendingExpenses = pendingResult.status === 'fulfilled' ? pendingResult.value : [];

      const apiPopups = Array.isArray(resolvedPopups) ? resolvedPopups : [];
      let finalPopups = [...apiPopups];

      if (pendingExpenses && pendingExpenses.length > 0) {
        const expensePopup = {
          id: -1,
          headline: '⚠️ Você possui reembolsos pendentes!',
          content:
            'Identificamos que você possui solicitações de reembolso pendentes. Acesse a tela de reembolsos lançados e edite as solicitações conforme indicado.',
          confirm_button_text: 'Ir para reembolsos',
          type: 'PendingExpense' as const,
          popup_is_open: true,
          onConfirm: () => navigate('/reembolso?status=Pendente'),
        };

        finalPopups = finalPopups.filter((p) => p.id !== -1);
        finalPopups.push(expensePopup);
      }

      setPopupsList(finalPopups);
    } catch (error) {
      setPopupsList([]);
    }
  };

  useEffect(() => {
    let timerInterval: NodeJS.Timeout;
    let timeoutDelay: NodeJS.Timeout;

    if (isLoggedIn && userInformation) {
      loadAllPopups();
      timerInterval = setInterval(() => {
        loadAllPopups();
      }, INTERVAL_TIME_IN_MILLISECONDS);
      timeoutDelay = setTimeout(() => {
        setIsLogged(true);
      }, TIMEOUT_TIME_IN_MILLISECONDS);
    } else {
      setIsLogged(false);
    }
    return () => {
      clearInterval(timerInterval);
      clearTimeout(timeoutDelay);
    };
  }, [isLoggedIn, userInformation?.user_id, isPartnerRoute]);

  if (popupsToDisplay.length === 0) return null;

  interface IPopups {
    popup: PopupsProps;
  }

  const Popups = ({ popup }: IPopups) => (
    <Container key={popup.id} type={popup.type}>
      <Content type={popup.type}>
        {popup.type === 'Default' && (
          <PopupTemplate1
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            consentRequired={popup?.consent_required || false}
            consentText={popup?.consent_text || ''}
            consentDescription={popup?.consent_description || ''}
          />
        )}
        {popup.type === 'InfoWithImage' && (
          <PopupTemplate2
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            imageUrl={popup?.image_url || '#'}
          />
        )}
        {popup.type === 'PendingExpense' && (
          <PopupTemplate3
            id={popup.id}
            headline={popup.headline}
            content={popup.content}
            confirmButtonText={popup.confirm_button_text}
            onConfirm={popup.onConfirm}
          />
        )}
      </Content>
    </Container>
  );

  return (
    <Backdrop isLogged={isLogged}>
      {popupsToDisplay.map((popup) => (
        <Popups key={popup.id} popup={popup} />
      ))}
    </Backdrop>
  );
};

export default CustomPopupsLayout;
